/**
 * Frontend subscription utilities.
 * Centralises the "is this user Pro?" check so every page uses the same logic.
 */
import * as trainerAccess from "../../shared/trainerAccess.js";

const { hasActivePaidAccess, hasProFeatureAccess, shouldHideProStatusChrome } =
  trainerAccess;

/**
 * Returns true when the user should see Pro feature surfaces.
 * Includes FREE_FIRST launch unlock via user.freeFirst.unlockProSurfaces
 * (from /api/auth/me). Paid entitlement alone remains hasActivePaidAccess.
 *
 * @param {Object|null} user - user object (from localStorage, API, or state)
 * @returns {boolean}
 */
export function isProUser(user) {
  return hasProFeatureAccess(user);
}

/** Settings Account eyebrow while checkout is paused. */
export const LAUNCH_ACCESS_TITLE = "Launch access";

/** Settings Account fallback. Do not invent Pro comparison caps here. */
export const LAUNCH_ACCESS_NOTE =
  "Paid Pro checkout is paused during launch. Enjoy raised free limits.";

/** Sidebar and profile chip. Do not surface billing-pause copy to a new rider. */
export const LAUNCH_PLAN_TITLE = "Free during launch";
export const LAUNCH_PLAN_NOTE =
  "Training plans, fueling, and bonk tools are included.";

/**
 * Live launch-free note from session messaging, else Settings Account copy.
 * @param {Object|null} user
 * @returns {string}
 */
export function resolveLaunchAccessNote(user) {
  const note = user?.freeFirst?.messaging?.pricingNote;
  return typeof note === "string" && note.trim() ? note : LAUNCH_ACCESS_NOTE;
}

/**
 * Decorative Pro Member / Unlimited chrome. Launch unlock is not paid Pro.
 * @param {Object|null} user
 * @returns {boolean}
 */
export function shouldShowProMemberChrome(user) {
  return hasActivePaidAccess(user);
}

/**
 * Launch-free Account chrome (sidebar / profile). Not a paid subscriber.
 * @param {Object|null} user
 * @returns {boolean}
 */
export function shouldShowLaunchAccessChrome(user) {
  return !hasActivePaidAccess(user) && shouldHideProStatusChrome(user);
}

export { hasActivePaidAccess, hasProFeatureAccess, shouldHideProStatusChrome };
